import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../pd-form-row.js";
import "../pd-form-fieldset.js";
import "../pd-checkbox.js";
import "../pd-color-picker.js";
import "../pd-input.js";
import "../pd-input-area.js";
import "../pd-input-url.js";
import "../pd-radio-group.js";
import "../pd-select.js";
import { pdGenericFormStyles } from "./pd-generic-form.styles.js";
import { LitElement, html } from "lit";
import { property, state } from "lit/decorators.js";
import { pdIcons } from "@progressive-development/pd-icon";
import "@progressive-development/pd-icon/pd-icon";
import { msg } from "@lit/localize";
import { ifDefined } from "lit/directives/if-defined.js";
import { pdSchema } from "@progressive-development/pd-model";
//#region src/pd-generic-form/PdGenericForm.ts
var PdGenericForm = class PdGenericForm extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.schema = [];
		this.data = {};
		this.mode = "view";
		this.disabled = false;
		this._formValues = {};
		this._onFieldChange = (e) => {
			const name = e.detail?.name;
			if (name) this._formValues = {
				...this._formValues,
				[name]: e.detail.value
			};
		};
		this._qualifiers = /* @__PURE__ */ new Map();
		this._consumedQualifiers = /* @__PURE__ */ new Set();
	}
	static {
		this.styles = [pdGenericFormStyles];
	}
	connectedCallback() {
		super.connectedCallback();
		this.addEventListener("pd-form-element-change", this._onFieldChange);
	}
	disconnectedCallback() {
		this.removeEventListener("pd-form-element-change", this._onFieldChange);
		super.disconnectedCallback();
	}
	_isVisible(showWhen) {
		if (!showWhen) return true;
		const currentValue = this._formValues[showWhen.fieldId];
		if (currentValue !== void 0) return this._valuesMatch(currentValue, showWhen.equals);
		const fieldValue = this.data[showWhen.fieldId];
		if (!fieldValue) return false;
		return this._valuesMatch(fieldValue.value, showWhen.equals);
	}
	_valuesMatch(actual, expected) {
		if (actual === expected) return true;
		if (typeof expected === "boolean") {
			if (expected === true) return actual === "yes" || actual === "true";
			return actual === "no" || actual === "false";
		}
		return false;
	}
	/**
	* Extracts schema fields from raw form values
	* and converts them into typed SchemaFieldData.
	*
	* @param formValues - Raw values from pd-form-container.getValues().origin
	*/
	extractFormData(formValues) {
		const result = {};
		for (const entry of this.schema) if (pdSchema.isRow(entry)) for (const field of entry.fields) this._extractField(field, formValues, result);
		else if (pdSchema.isFieldset(entry)) {
			const nested = formValues[entry.name] ?? {};
			for (const row of entry.rows) for (const field of row) this._extractField(field, nested, result);
		} else this._extractField(entry, formValues, result);
		const byId = new Map(this._allFields().map((f) => [f.fieldId, f]));
		for (const field of this._allFields()) {
			if (field.displayAs !== "qualifier" || !field.showWhen) continue;
			const parent = byId.get(field.showWhen.fieldId);
			if (!parent || parent.displayAs === "qualifier") continue;
			const parentValue = result[parent.fieldId]?.value;
			if (!this._valuesMatch(parentValue, field.showWhen.equals)) delete result[field.fieldId];
		}
		return result;
	}
	_extractField(field, source, result) {
		if (!(field.fieldId in source)) return;
		const formValue = source[field.fieldId];
		let value;
		if (field.inputType === "tristate") {
			value = formValue === pdSchema.TRISTATE_YES || formValue === pdSchema.TRISTATE_NO ? formValue : pdSchema.TRISTATE_UNKNOWN;
			result[field.fieldId] = pdSchema.createSchemaFieldValue(value, field.type);
			return;
		}
		if (field.type === "boolean") value = formValue === "yes" || formValue === "true";
		else if (field.type === "number") value = parseFloat(String(formValue)) || 0;
		else value = formValue || "";
		result[field.fieldId] = pdSchema.createSchemaFieldValue(value, field.type);
	}
	render() {
		return this.mode === "edit" ? this._renderEdit() : this._renderView();
	}
	_renderView() {
		if (!this.schema.length) return html``;
		this._buildQualifierIndex();
		if (!(this.data && Object.keys(this.data).length > 0)) return html`<p class="empty-state">
        ${msg("Keine Daten vorhanden", { id: "pd.generic-form.no-data" })}
      </p>`;
		return html`
      <div class="view-list">
        ${this.schema.map((e) => {
			if (pdSchema.isRow(e)) {
				if (!this._isVisible(e.showWhen)) return html``;
				return html`${e.fields.map((f) => this._renderViewField(f))}`;
			}
			if (pdSchema.isFieldset(e)) {
				if (!this._isVisible(e.showWhen)) return html``;
				return html` ${e.legend ? html`<div class="view-fieldset-legend">${e.legend}</div>` : ""}
            ${e.rows.flat().map((f) => this._renderViewField(f))}`;
			}
			if (!this._isVisible(e.showWhen)) return html``;
			return this._renderViewField(e);
		})}
      </div>
    `;
	}
	/** The fixed tristate vocabulary — localized by the package, not the app. */
	_tristateOptions() {
		return [
			{
				value: pdSchema.TRISTATE_YES,
				name: msg("Ja", { id: "pd.generic-form.yes" })
			},
			{
				value: pdSchema.TRISTATE_NO,
				name: msg("Nein", { id: "pd.generic-form.no" })
			},
			{
				value: pdSchema.TRISTATE_UNKNOWN,
				name: msg("Unbekannt", { id: "pd.generic-form.unknown" })
			}
		];
	}
	/**
	* Qualifier wiring: a field with displayAs "qualifier" is consumed by
	* its showWhen parent. Invalid configurations (no showWhen, unknown
	* parent, parent is itself a qualifier) degrade to a normal own row —
	* data never disappears over a config mistake.
	*/
	_buildQualifierIndex() {
		const all = this._allFields();
		const byId = new Map(all.map((f) => [f.fieldId, f]));
		this._qualifiers = /* @__PURE__ */ new Map();
		this._consumedQualifiers = /* @__PURE__ */ new Set();
		for (const field of all) {
			if (field.displayAs !== "qualifier") continue;
			const parent = field.showWhen && byId.get(field.showWhen.fieldId);
			if (!parent || parent.displayAs === "qualifier") {
				console.warn(`pd-generic-form: invalid qualifier config on "${field.fieldId}" — rendering as own row`);
				continue;
			}
			const list = this._qualifiers.get(parent.fieldId) ?? [];
			list.push(field);
			this._qualifiers.set(parent.fieldId, list);
			this._consumedQualifiers.add(field.fieldId);
		}
	}
	_allFields() {
		const fields = [];
		for (const entry of this.schema) if (pdSchema.isRow(entry)) fields.push(...entry.fields);
		else if (pdSchema.isFieldset(entry)) entry.rows.forEach((row) => fields.push(...row));
		else fields.push(entry);
		return fields;
	}
	/**
	* " · value" chain of this field's active qualifiers, in schema order.
	* A qualifier only appears while the parent actually holds its showWhen
	* value (stale stored data stays invisible), and an empty qualifier is
	* skipped silently — the separator belongs to the value, so the row
	* never ends in a dangling "·".
	*/
	_qualifierSuffix(field) {
		const qualifiers = this._qualifiers.get(field.fieldId);
		if (!qualifiers?.length) return "";
		const parentValue = this.data[field.fieldId]?.value;
		const parts = [];
		for (const qualifier of qualifiers) {
			if (!this._valuesMatch(parentValue, qualifier.showWhen.equals)) continue;
			const text = this._qualifierText(qualifier);
			if (text) parts.push(text);
		}
		return parts.map((part) => ` · ${part}`).join("");
	}
	_qualifierText(field) {
		const raw = this.data[field.fieldId]?.value;
		if (raw === void 0 || raw === null || raw === "") return void 0;
		let text = String(raw);
		if (field.options) text = field.options.find((o) => o.value === raw)?.name ?? text;
		return field.unit ? `${text} ${field.unit}` : text;
	}
	_renderViewField(field) {
		if (this._consumedQualifiers.has(field.fieldId)) return html``;
		if (field.inputType === "tristate") return this._renderTristateViewField(field);
		const fieldValue = this.data[field.fieldId];
		const displayValue = this._formatForView(field, fieldValue);
		return html`
      <div
        class="view-item ${this._isCompactRow(field) ? "" : "view-item--full"}"
      >
        <div class="view-label">
          ${field.label}
          ${field.description ? html`<span class="field-description"
                  >${field.description}</span
                >` : ""}
        </div>
        <div class="view-value">
          ${displayValue}${this._qualifierSuffix(field)}
        </div>
      </div>
    `;
	}
	/**
	* Tri-state row: state glyph (pd-icon) + localized value. Farbe liegt
	* AUSSCHLIESSLICH auf dem Glyph (check grün, x rot, unknown muted) —
	* Ja und Nein bleiben als Text primär, sonst liest sich der Block wie
	* eine Mängelliste. Bei Unbekannt wird nur der WERT gedämpft, das
	* Label behält seine Tonalität (Revision 2026-08-18 nach Testfeedback:
	* das frühere Ganzzeilen-Dämpfen ließ Feldname und Inhalt zu einer
	* grauen Fläche verschmelzen). Der Zustand ist dreifach kodiert —
	* Glyphform, Wort, Farbe — und bleibt damit farbfehlsicht-tauglich.
	*/
	_renderTristateViewField(field) {
		const raw = pdSchema.getSchemaFieldValue(this.data, field.fieldId);
		const value = raw === pdSchema.TRISTATE_YES || raw === pdSchema.TRISTATE_NO ? raw : pdSchema.TRISTATE_UNKNOWN;
		const icons = {
			[pdSchema.TRISTATE_YES]: pdIcons.ICON_CHECK,
			[pdSchema.TRISTATE_NO]: pdIcons.ICON_CANCEL,
			[pdSchema.TRISTATE_UNKNOWN]: pdIcons.ICON_HELP
		};
		const name = this._tristateOptions().find((o) => o.value === value).name;
		const unknown = value === pdSchema.TRISTATE_UNKNOWN;
		return html`
      <div
        class="view-item ${unknown ? "view-item--unknown" : ""} ${this._isCompactRow(field) ? "" : "view-item--full"}"
      >
        <div class="view-label">${field.label}</div>
        <div class="view-value view-value--state">
          <pd-icon
            class="state-icon state-icon--${value}"
            icon="${icons[value]}"
            aria-hidden="true"
          ></pd-icon>
          <span>${name}${this._qualifierSuffix(field)}</span>
        </div>
      </div>
    `;
	}
	/**
	* Row width in the view grid: compact fields (booleans, numbers, single
	* selects) share a column, text-like fields span the full width — a
	* parent row inherits "full" when any of its qualifiers is text-like.
	* Derived from the field types — deliberately never from the concrete
	* value's length, so the layout is identical across records.
	*/
	_isCompactRow(field) {
		const qualifiers = this._qualifiers.get(field.fieldId) ?? [];
		return this._isCompactField(field) && qualifiers.every((q) => this._isCompactField(q));
	}
	_isCompactField(field) {
		if (field.type === "boolean" || field.type === "number") return true;
		return field.inputType === "radio" || field.inputType === "tristate" || field.inputType === "select" || field.inputType === "number";
	}
	_formatForView(field, fieldValue) {
		if (field.type === "boolean") return pdSchema.formatSchemaFieldValue(fieldValue, {
			boolTrue: msg("Ja", { id: "pd.generic-form.yes" }),
			boolFalse: msg("Nein", { id: "pd.generic-form.no" })
		});
		const value = pdSchema.formatSchemaFieldValue(fieldValue);
		if (field.inputType === "radio" && field.options && value !== "–") {
			const match = field.options.find((o) => o.value === value);
			if (match) return match.name;
		}
		switch (field.viewFormat) {
			case "mailto": return value !== "–" ? html`<a href="mailto:${value}">${value}</a>` : value;
			case "tel": return value !== "–" ? html`<a href="tel:${value}">${value}</a>` : value;
			case "link": return value !== "–" ? html`<a href="${value}" target="_blank" rel="noopener">${value}</a>` : value;
			default: return field.unit && value !== "–" ? `${value} ${field.unit}` : value;
		}
	}
	_renderEdit() {
		return html`${this.schema.map((e) => {
			if (pdSchema.isRow(e)) return this._isVisible(e.showWhen) ? this._renderRow(e) : html``;
			if (pdSchema.isFieldset(e)) return this._isVisible(e.showWhen) ? this._renderFieldset(e) : html``;
			return this._isVisible(e.showWhen) ? this._renderFieldInRow(e) : html``;
		})}`;
	}
	static {
		this._fieldTypeMap = {
			number: "number",
			email: "mail",
			phone: "phone"
		};
	}
	/** Single field wrapped in its own pd-form-row. */
	_renderFieldInRow(field) {
		return html`
      <pd-form-row> ${this._renderFieldControl(field)} </pd-form-row>
    `;
	}
	/** Multiple fields in a single pd-form-row. */
	_renderRow(row) {
		return html`
      <pd-form-row>
        ${row.fields.map((f) => this._renderFieldControl(f))}
      </pd-form-row>
    `;
	}
	/** Fieldset with nested pd-form-rows per row entry. */
	_renderFieldset(fs) {
		return html`
      <pd-form-row>
        <pd-form-fieldset
          name="${fs.name}"
          legend="${ifDefined(fs.legend)}"
          span="${fs.span || "full"}"
        >
          ${fs.rows.map((row) => html`
              <pd-form-row>
                ${row.map((f) => this._renderFieldControl(f))}
              </pd-form-row>
            `)}
        </pd-form-fieldset>
      </pd-form-row>
    `;
	}
	/** Renders a field control (without pd-form-row wrapper), respecting field.span. */
	/**
	* Control plus optional description. The wrapper carries the field's `span`
	* so the row grid keeps working (the ::slotted rules of pd-form-row only
	* apply to direct slot children), and the control inside is stretched to the
	* wrapper's width.
	*/
	_renderFieldControl(field) {
		const control = this._renderControl(field);
		if (!field.description) return control;
		return html`
      <div class="with-description" span="${field.span || "full"}">
        ${control}
        <span class="field-description">${field.description}</span>
      </div>
    `;
	}
	_renderControl(field) {
		switch (field.inputType) {
			case "radio-yes-no": return this._renderBooleanControl(field);
			case "radio": return this._renderRadioControl(field);
			case "tristate": return this._renderRadioControl({
				...field,
				options: this._tristateOptions()
			});
			case "textarea": return this._renderTextareaControl(field);
			case "text":
			case "number":
			case "email":
			case "phone": return this._renderInputControl(field);
			case "url": return this._renderUrlControl(field);
			case "checkbox": return this._renderCheckboxControl(field, "CHECK");
			case "switch": return this._renderCheckboxControl(field, "SWITCH");
			case "color": return this._renderColorControl(field);
			case "select": return this._renderSelectControl(field);
			default:
				console.warn(`pd-generic-form: Unsupported inputType "${field.inputType}"`);
				return html``;
		}
	}
	_renderRadioControl(field) {
		const initValue = pdSchema.getSchemaFieldValue(this.data, field.fieldId) || "";
		return html`
      <pd-radio-group
        id="${field.fieldId}"
        valueName="${field.fieldId}"
        label="${field.label}"
        span="${field.span || "full"}"
        ?required="${field.required}"
        ?disabled="${this.disabled}"
        initValue="${initValue}"
      >
        ${(field.options || []).map((opt) => html`
            <pd-checkbox
              checkType="RADIO"
              valueName="${opt.value}"
              initValue="${(initValue === opt.value).toString()}"
              >${opt.name}</pd-checkbox
            >
          `)}
      </pd-radio-group>
    `;
	}
	_renderBooleanControl(field) {
		const initValue = pdSchema.getSchemaFieldValue(this.data, field.fieldId) === true;
		return html`
      <pd-radio-group
        id="${field.fieldId}"
        valueName="${field.fieldId}"
        label="${field.label}"
        span="${field.span || "full"}"
        ?required="${field.required}"
        ?disabled="${this.disabled}"
        initValue="${initValue ? "yes" : "no"}"
      >
        <pd-checkbox valueName="yes" initValue="${initValue.toString()}"
          >${msg("Ja", { id: "pd.generic-form.yes" })}</pd-checkbox
        >
        <pd-checkbox valueName="no" initValue="${(!initValue).toString()}"
          >${msg("Nein", { id: "pd.generic-form.no" })}</pd-checkbox
        >
      </pd-radio-group>
    `;
	}
	_renderTextareaControl(field) {
		const initValue = pdSchema.getSchemaFieldValue(this.data, field.fieldId) || "";
		return html`
      <pd-input-area
        id="${field.fieldId}"
        valueName="${field.fieldId}"
        span="${field.span || "full"}"
        minlength="${ifDefined(field.minlength)}"
        maxlength="${ifDefined(field.maxlength)}"
        label="${field.label}"
        placeHolder="${field.placeholder || ""}"
        ?required="${field.required}"
        ?disabled="${this.disabled}"
        initValue="${initValue}"
        .aiOptions="${field.aiOptions}"
      ></pd-input-area>
    `;
	}
	_renderSelectControl(field) {
		const initValue = pdSchema.getSchemaFieldValue(this.data, field.fieldId) || "";
		return html`
      <pd-select
        id="${field.fieldId}"
        valueName="${field.fieldId}"
        span="${field.span || "full"}"
        label="${field.label}"
        .values="${field.options || []}"
        ?required="${field.required}"
        ?disabled="${this.disabled}"
        initValue="${initValue}"
      ></pd-select>
    `;
	}
	_renderCheckboxControl(field, checkType) {
		const initValue = pdSchema.getSchemaFieldValue(this.data, field.fieldId) === true;
		return html`
      <pd-checkbox
        id="${field.fieldId}"
        valueName="${field.fieldId}"
        span="${field.span || "full"}"
        checkType="${checkType}"
        ?required="${field.required}"
        ?disabled="${this.disabled}"
        initValue="${initValue.toString()}"
        >${field.label}</pd-checkbox
      >
    `;
	}
	_renderColorControl(field) {
		const initValue = pdSchema.getSchemaFieldValue(this.data, field.fieldId) || "";
		return html`
      <pd-color-picker
        id="${field.fieldId}"
        valueName="${field.fieldId}"
        span="${field.span || "full"}"
        label="${field.label}"
        placeholder="${field.placeholder || "000000"}"
        ?required="${field.required}"
        ?disabled="${this.disabled}"
        initValue="${initValue}"
      ></pd-color-picker>
    `;
	}
	_renderUrlControl(field) {
		const initValue = pdSchema.getSchemaFieldValue(this.data, field.fieldId) || "";
		return html`
      <pd-input-url
        id="${field.fieldId}"
        valueName="${field.fieldId}"
        span="${field.span || "full"}"
        label="${field.label}"
        placeHolder="${field.placeholder || ""}"
        ?required="${field.required}"
        ?disabled="${this.disabled}"
        initValue="${initValue}"
      ></pd-input-url>
    `;
	}
	_renderInputControl(field) {
		const initValue = pdSchema.getSchemaFieldValue(this.data, field.fieldId)?.toString() || "";
		const fieldType = PdGenericForm._fieldTypeMap[field.inputType] || "text";
		return html`
      <pd-input
        id="${field.fieldId}"
        valueName="${field.fieldId}"
        span="${field.span || "full"}"
        minlength="${ifDefined(field.minlength)}"
        maxlength="${ifDefined(field.maxlength)}"
        label="${field.label}"
        placeHolder="${field.placeholder || ""}"
        fieldType="${fieldType}"
        autoCompleteName="${ifDefined(field.autocomplete)}"
        ?required="${field.required}"
        ?disabled="${this.disabled}"
        reserve-label
        initValue="${initValue}"
        .aiOptions="${field.aiOptions}"
      ></pd-input>
    `;
	}
};
__decorate([property({ type: Array })], PdGenericForm.prototype, "schema", void 0);
__decorate([property({ type: Object })], PdGenericForm.prototype, "data", void 0);
__decorate([property({
	type: String,
	reflect: true
})], PdGenericForm.prototype, "mode", void 0);
__decorate([property({ type: Boolean })], PdGenericForm.prototype, "disabled", void 0);
__decorate([state()], PdGenericForm.prototype, "_formValues", void 0);
//#endregion
export { PdGenericForm };
